/* CRUX × EON — storefront learning client (gated).
 * Sends lightweight behavioural telemetry to EON and exposes a recommendations
 * fetch helper. No-op unless window.EON_STOREFRONT.enabled + .base are set
 * (theme setting "Enable EON storefront learning"). Fire-and-forget, fails open,
 * never blocks rendering. Telemetry is advisory only. */
(function () {
  'use strict';
  var C = window.EON_STOREFRONT;
  if (!C || !C.enabled || !C.base) return;

  var BASE = String(C.base).replace(/\/+$/, '');
  var SHOP = encodeURIComponent(C.shop || '');
  var ctx = C.context || {};

  function uuid() {
    try { return (crypto.randomUUID && crypto.randomUUID()) ||
      ('a' + Date.now().toString(36) + Math.random().toString(36).slice(2)); }
    catch (e) { return 'a' + Date.now().toString(36) + Math.random().toString(36).slice(2); }
  }
  function stable(store, key) {
    try { var v = store.getItem(key); if (!v) { v = uuid(); store.setItem(key, v); } return v; }
    catch (e) { return null; }
  }
  var ANON = stable(window.localStorage, 'eon_anon');
  var SESS = stable(window.sessionStorage, 'eon_sess');

  // Meta Pixel <-> EON CAPI dedup: ONE id per logical action, shared by both
  // paths (the Pixel's eventID AND EON's event_uuid). Meta collapses a Pixel hit
  // and the server-side CAPI hit only when (event_id, event_name) match — so this
  // id is NEVER random per-send on the CAPI side. fbp/fbc ride along so anonymous
  // server-side events still match. The Pixel is optional (no-op if not loaded).
  function getCookie(n) {
    try { var p = ('; ' + document.cookie).split('; ' + n + '=');
      return p.length === 2 ? p.pop().split(';').shift() : null; }
    catch (e) { return null; }
  }
  var META_EVT = { product_view: 'ViewContent', collection_view: 'ViewContent',
    search: 'Search', add_to_cart: 'AddToCart', begin_checkout: 'InitiateCheckout' };

  // ── ad click-ids ────────────────────────────────────────────
  // Capture platform click ids from the landing URL so EON's server-side CAPI
  // can attribute anonymous ad-click sessions: fbclid (Meta), ttclid (TikTok),
  // ScCid (Snap). Persisted in localStorage with capture time so follow-on
  // pageviews (no query param) still carry them; expired ids are dropped.
  function clickId(param, key, days) {
    try {
      var m = new RegExp('[?&]' + param + '=([^&#]+)', 'i').exec(location.search);
      var now = Date.now();
      if (m && m[1]) {
        var o = { v: decodeURIComponent(m[1]), t: now };
        try { window.localStorage.setItem(key, JSON.stringify(o)); } catch (e) {}
        return o;
      }
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || !s.v || (now - (s.t || 0)) > days * 864e5) return null;
      return s;
    } catch (e) { return null; }
  }
  var FBCLID = clickId('fbclid', 'eon_fbclid', 7);    // Meta click window
  var TTCLID = clickId('ttclid', 'eon_ttclid', 30);   // TikTok Events API user.ttclid
  var SCCID  = clickId('ScCid',  'eon_sccid',  30);   // Snap CAPI click_id

  // ── session first-touch (attribution, 2026-07-24) ───────────
  // Same key + shape as the WA-button snippet's capture — whichever runs
  // first writes; the other finds the key and skips. utm_*, ad click-ids,
  // external referrer host + landing path, once per tab-session. Every
  // event beacon carries it (payload.first_touch), so any on-site slice —
  // views, carts, checkout starts — groups by traffic source server-side.
  try {
    if (!sessionStorage.getItem('eon_first_touch')) {
      var q0 = new URLSearchParams(location.search), ft0 = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
       'utm_term', 'fbclid', 'ttclid', 'gclid'].forEach(function (k) {
        var v0 = q0.get(k);
        if (v0) ft0[k] = v0.slice(0, 200);
      });
      try {
        if (document.referrer) {
          var rh0 = new URL(document.referrer).hostname;
          if (rh0 && rh0 !== location.hostname) ft0.ref_host = rh0.slice(0, 100);
        }
      } catch (e2) {}
      if (Object.keys(ft0).length) {
        ft0.landing = (location.pathname || '').slice(0, 150);
        sessionStorage.setItem('eon_first_touch', JSON.stringify(ft0));
      }
    }
  } catch (e) {}
  function firePixel(ev, eid) {
    try {
      if (!window.fbq || !META_EVT[ev.event_type]) return;
      // currency is only valid ALONGSIDE value — sending it alone made the
      // Pixel log "Invalid parameter format for currency" on every event and
      // gave Meta no value signal for optimization (vet 2026-07-11).
      var cd = { content_type: 'product' };
      var val = Number(ev.value);
      if (isFinite(val) && val > 0) { cd.value = val; cd.currency = 'NGN'; }
      if (ev.product_shopify_id) cd.content_ids = [String(ev.product_shopify_id)];
      else if (ev.sku) cd.content_ids = [String(ev.sku)];
      if (ev.collection_handle) cd.content_category = ev.collection_handle;
      if (ev.search_query) cd.search_string = ev.search_query;
      window.fbq('track', META_EVT[ev.event_type], cd, { eventID: eid });
    } catch (e) { /* the Pixel must never block telemetry */ }
  }

  // Item 3 (identity capture, 2026-07-24): stamp our session id into the
  // Shopify cart so the order webhook can retro-link this session's events
  // to the buyer's identity at checkout (cart attributes surface as
  // note_attributes on the order — read by session_identity.link_order_session).
  // Once per tab-session, fired at cart moments (a cart is guaranteed then).
  function stampCartSession() {
    try {
      if (sessionStorage.getItem('eon_cart_stamped')) return;
      sessionStorage.setItem('eon_cart_stamped', '1');
      // keepalive: the begin_checkout call races navigation to Shopify's
      // hosted checkout — without it the request is torn down with the
      // page. Flag clears on BOTH network failure and non-2xx, so a
      // failed stamp retries on the next cart moment.
      fetch('/cart/update.js', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _eon_session: SESS } })
      }).then(function (r) {
        if (!r.ok) { try { sessionStorage.removeItem('eon_cart_stamped'); } catch (e) {} }
      }).catch(function () {
        try { sessionStorage.removeItem('eon_cart_stamped'); } catch (e) {}
      });
    } catch (e) { /* never block */ }
  }

  function send(ev) {
    if (!ev || !ev.event_type) return;
    if (ev.event_type === 'add_to_cart' || ev.event_type === 'begin_checkout') {
      stampCartSession();
    }
    var eid = uuid();
    ev.event_uuid = eid;                       // EON column; CAPI reuses this exact id
    var pl = ev.payload || {};
    pl.event_id = eid;                         // payload fallback the CAPI forwarder reads
    var fbp = getCookie('_fbp'), fbc = getCookie('_fbc');
    // No _fbc cookie (Pixel absent or blocked) but the visit came from a Meta
    // ad click: derive fbc in Meta's own cookie format, fb.1.<capture-ms>.<fbclid>,
    // using the time the click id was FIRST seen (not per-send).
    if (!fbc && FBCLID) fbc = 'fb.1.' + FBCLID.t + '.' + FBCLID.v;
    if (fbp) pl.fbp = fbp;
    if (fbc) pl.fbc = fbc;
    if (TTCLID) pl.ttclid = TTCLID.v;
    if (SCCID) pl.sc_click_id = SCCID.v;
    // value rides in the payload too so EON's server-side CAPI forward can
    // send custom_data.value/currency symmetrically with the client Pixel.
    // (The forwarder hardcodes NGN, but the payload stays self-describing.)
    if (isFinite(Number(ev.value)) && Number(ev.value) > 0) {
      pl.value = Number(ev.value);
      pl.currency = 'NGN';
    }
    // Platform browser-id cookies (set by the TikTok/Snap pixels when present)
    // ride along raw — they lift server-side match quality like fbp does.
    var ttp = getCookie('_ttp');   if (ttp) pl.ttp = ttp;
    var scid = getCookie('_scid'); if (scid) pl.sc_scid = scid;
    // Session first-touch rides on EVERY event (read per-send: the capture
    // may land after module init when the WA snippet writes it first).
    try {
      var ft1 = sessionStorage.getItem('eon_first_touch');
      if (ft1) pl.first_touch = JSON.parse(ft1);
    } catch (e) {}
    ev.payload = pl;
    firePixel(ev, eid);                        // client Pixel with the SAME id (no-op if absent)
    var body = JSON.stringify({
      events: [ev],
      anonymous_client_id: ANON,
      session_id: SESS,
      shopify_customer_id: C.customerId || null
    });
    var url = BASE + '/api/storefront/events?shop=' + SHOP;
    try {
      // sendBeacon = no preflight, survives page unload
      if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
    } catch (e) { /* fall through */ }
    try {
      fetch(url, { method: 'POST', body: body, keepalive: true, mode: 'cors',
        headers: { 'Content-Type': 'text/plain' } }).catch(function () {});
    } catch (e) { /* swallow */ }
  }

  // ── page-level events ──────────────────────────────────────
  if (ctx.type === 'product' && ctx.productId) {
    send({ event_type: 'product_view', product_shopify_id: ctx.productId,
           value: ctx.price, page_url: location.href });
  } else if (ctx.type === 'collection' && ctx.collection) {
    send({ event_type: 'collection_view', collection_handle: ctx.collection,
           page_url: location.href });
  } else if (ctx.type === 'search' && ctx.query) {
    send({ event_type: 'search', search_query: ctx.query, page_url: location.href });
  }

  // ── add-to-cart ────────────────────────────────────────────
  // Prefer the native theme pub/sub; fall back to product-form submit on PDP.
  // Value = the CART-TOTAL DELTA read from /cart.js around each cartUpdate:
  // it prices any add source (PDP form, drawer +, card tray) without per-variant
  // plumbing, and a zero/negative delta (qty decrease, removal) fires nothing —
  // which also stops cart-page qty edits from counting as AddToCart.
  var cartTotal = null; // NGN units (Shopify cents / 100); null until first read
  var cartSeq = 0;      // stale-read guard: only the LATEST mutation's read applies
  function readCartTotal() {
    return fetch('/cart.js').then(function (r) { return r.json(); })
      .then(function (c) { return (c && typeof c.total_price === 'number') ? c.total_price / 100 : null; })
      .catch(function () { return null; });
  }
  readCartTotal().then(function (t) { if (cartTotal === null && cartSeq === 0) cartTotal = t; });
  function onCartMutation(pid) {
    var seq = ++cartSeq;
    readCartTotal().then(function (t) {
      // A newer mutation issued a newer read — let that one settle the ledger.
      // Rapid double-adds therefore collapse into ONE event carrying the
      // combined delta: value totals stay exact, no double-count, and a stale
      // response can never regress cartTotal backwards.
      if (seq !== cartSeq) return;
      if (t === null) {
        send({ event_type: 'add_to_cart', product_shopify_id: pid, page_url: location.href });
        return;
      }
      var prev = cartTotal;
      cartTotal = t;
      var delta = (prev === null) ? null : Math.round((t - prev) * 100) / 100;
      if (delta !== null && delta <= 0) return; // qty decrease / removal / no-op
      send({ event_type: 'add_to_cart', product_shopify_id: pid,
             value: (delta === null ? ctx.price : delta), page_url: location.href });
    });
  }
  try {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS &&
        window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        var pid = ctx.productId || null;
        try {
          if (payload && payload.productId) pid = payload.productId;
        } catch (e) {}
        onCartMutation(pid);
      });
    } else if (ctx.type === 'product' && ctx.productId) {
      document.addEventListener('submit', function (e) {
        var f = e.target;
        if (f && f.action && /\/cart\/add/.test(f.action)) {
          send({ event_type: 'add_to_cart', product_shopify_id: ctx.productId,
                 value: ctx.price, page_url: location.href });
          // keep the running total fresh so a follow-on begin_checkout
          // doesn't beacon the pre-add figure
          readCartTotal().then(function (t) { if (t !== null) cartTotal = t; });
        }
      }, true);
    }
  } catch (e) { /* swallow */ }

  // ── begin-checkout ─────────────────────────────────────────
  // InitiateCheckout was mapped but never fired (vet 2026-07-11). Any click on
  // a checkout control beacons it with the last-known cart total; sendBeacon
  // survives the navigation. Capture-phase so theme handlers can't swallow it.
  // 60s debounce so slow-checkout re-clicks don't multiply the event, and
  // modifier-key clicks (new-tab intent, no navigation here) don't fire.
  // NOTE: if the Shopify Facebook channel app also emits InitiateCheckout on
  // this pixel, counts double (different event ids — dedup can't collapse
  // them); check Events Manager after go-live and drop one source.
  try {
    document.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var el = e.target && e.target.closest &&
        e.target.closest('a[href*="/checkout"], button[name="checkout"], [data-checkout-button]');
      if (!el) return;
      try {
        var last = Number(sessionStorage.getItem('eon_ic_ts') || 0);
        if (Date.now() - last < 60e3) return;
        sessionStorage.setItem('eon_ic_ts', String(Date.now()));
      } catch (err) { /* storage blocked — fire anyway */ }
      send({ event_type: 'begin_checkout', value: cartTotal || undefined,
             page_url: location.href });
    }, true);
  } catch (e) { /* swallow */ }

  // ── event beacon (sections can call this) ──────────────────
  // window.EON.track('eon_look_served', {product_ids:[...]}) — fire a custom
  // storefront event so EON learns from on-page behaviour (look impressions,
  // look/card clicks). Reuses send(): sendBeacon, shared anon/session ids,
  // fire-and-forget, no-op when EON is disabled (this whole IIFE returns early).
  window.EON = window.EON || {};
  window.EON.track = function (type, payload) {
    try { if (type) send({ event_type: type, payload: payload || {} }); }
    catch (e) { /* telemetry must never throw into the page */ }
  };

  // ── recommendations helper (sections can call this) ────────
  // window.EON.getRecommendations({limit, excludeProductId}) -> Promise<products[]>
  window.EON.getRecommendations = function (opts) {
    opts = opts || {};
    var q = '?shop=' + SHOP + '&anonymous_client_id=' + encodeURIComponent(ANON || '') +
            '&limit=' + (opts.limit || 12) +
            (opts.excludeProductId ? '&exclude_product_id=' + encodeURIComponent(opts.excludeProductId) : '');
    return fetch(BASE + '/api/storefront/recommendations' + q, { mode: 'cors' })
      .then(function (r) { return r.json(); })
      .then(function (j) { return (j && j.ok && j.products) ? j.products : []; })
      .catch(function () { return []; });
  };
})();
